// src/pages/admin/NewsEditorPage.tsx
// ============================================================
// Summit League v2 — Admin News Editor Page
//
// Dual-purpose: create new post or edit existing.
// URL: /admin/news/new (create) or /admin/news/:postId (edit)
//
// Features:
//   - Title, content (textarea), excerpt
//   - Categories and tags as comma-separated input
//   - Publish/unpublish toggle
//   - SEO meta fields (collapsible)
//   - Save as draft or publish
//   - Delete (with confirmation)
// ============================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosClient from '../../api/axiosClient';
import Button from '../../components/ui/Button';

import '../../styles/admin-news.css';

interface PostData {
  title: string;
  content: string;
  excerpt: string;
  featured_image_url: string;
  categories: string;
  tags: string;
  is_published: boolean;
  meta_title: string;
  meta_description: string;
}

const EMPTY_POST: PostData = {
  title: '',
  content: '',
  excerpt: '',
  featured_image_url: '',
  categories: '',
  tags: '',
  is_published: false,
  meta_title: '',
  meta_description: '',
};

export default function NewsEditorPage() {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const isNew = postId === 'new' || !postId;

  const [form, setForm] = useState<PostData>(EMPTY_POST);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSeo, setShowSeo] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch existing post
  const fetchPost = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const res = await axiosClient.get(`/admin/news/${postId}`);
      const p = res.data.data;
      setForm({
        title: p.title || '',
        content: p.content || '',
        excerpt: p.excerpt || '',
        featured_image_url: p.featured_image_url || '',
        categories: (p.categories || []).join(', '),
        tags: (p.tags || []).join(', '),
        is_published: p.is_published || false,
        meta_title: p.meta_title || '',
        meta_description: p.meta_description || '',
      });
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load post');
    } finally {
      setLoading(false);
    }
  }, [postId, isNew]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  // Clear success after 3s
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(t);
  }, [success]);

  const handleChange = (field: keyof PostData, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const parseList = (str: string): string[] =>
    str.split(',').map((s) => s.trim()).filter(Boolean);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || isNew) return;

    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await axiosClient.post(`/admin/news/${postId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      handleChange('featured_image_url', res.data.data.featured_image_url);
      setSuccess('Image uploaded!');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Image upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleImageDelete = async () => {
    if (!form.featured_image_url || isNew) return;
    setUploading(true);
    setError('');
    try {
      await axiosClient.delete(`/admin/news/${postId}/image`);
      handleChange('featured_image_url', '');
      setSuccess('Image removed.');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to remove image');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (publish?: boolean) => {
    setError('');
    setSaving(true);

    try {
      const payload: Record<string, unknown> = {
        title: form.title,
        content: form.content,
        excerpt: form.excerpt || undefined,
        featured_image_url: form.featured_image_url || undefined,
        categories: parseList(form.categories),
        tags: parseList(form.tags),
        is_published: publish !== undefined ? publish : form.is_published,
        meta_title: form.meta_title || undefined,
        meta_description: form.meta_description || undefined,
      };

      if (isNew) {
        const res = await axiosClient.post('/admin/news', payload);
        const newId = res.data.data.post_id;
        setSuccess('Post created!');
        navigate(`/admin/news/${newId}`, { replace: true });
      } else {
        await axiosClient.put(`/admin/news/${postId}`, payload);
        setSuccess('Post updated!');
        if (publish !== undefined) {
          setForm((prev) => ({ ...prev, is_published: publish }));
        }
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this post? This action can be undone by a developer but not through the UI.')) return;

    setDeleting(true);
    try {
      await axiosClient.delete(`/admin/news/${postId}`);
      navigate('/admin/news', { replace: true });
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Delete failed');
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="sl-admin-news">
        <div className="sl-admin-loading">Loading post...</div>
      </div>
    );
  }

  return (
    <div className="sl-admin-news">
      {/* Header */}
      <div className="sl-news-editor-header">
        <button className="sl-user-back-btn" onClick={() => navigate('/admin/news')}>
          ← News
        </button>
        <h1 className="sl-admin-page-title">
          {isNew ? 'New Post' : 'Edit Post'}
        </h1>
      </div>

      {success && <div className="sl-user-success-banner">{success}</div>}
      {error && <div className="sl-admin-error">{error}</div>}

      <div className="sl-news-editor-layout">
        {/* Main content */}
        <div className="sl-news-editor-main">
          {/* Title */}
          <input
            type="text"
            className="sl-news-title-input"
            placeholder="Post title..."
            value={form.title}
            onChange={(e) => handleChange('title', e.target.value)}
          />

          {/* Content */}
          <textarea
            className="sl-news-content-input"
            placeholder="Write your post content here..."
            value={form.content}
            onChange={(e) => handleChange('content', e.target.value)}
            rows={18}
          />

          {/* Excerpt */}
          <div className="sl-news-field">
            <label className="sl-news-field-label">Excerpt</label>
            <textarea
              className="sl-news-excerpt-input"
              placeholder="Brief summary for post listings (optional)..."
              value={form.excerpt}
              onChange={(e) => handleChange('excerpt', e.target.value)}
              rows={3}
            />
          </div>

          {/* SEO Section (collapsible) */}
          <button
            type="button"
            className="sl-news-seo-toggle"
            onClick={() => setShowSeo(!showSeo)}
          >
            {showSeo ? '▾' : '▸'} SEO Settings
          </button>
          {showSeo && (
            <div className="sl-news-seo-section">
              <div className="sl-news-field">
                <label className="sl-news-field-label">Meta Title</label>
                <input
                  type="text"
                  className="sl-news-meta-input"
                  placeholder="SEO title (defaults to post title)"
                  value={form.meta_title}
                  onChange={(e) => handleChange('meta_title', e.target.value)}
                />
              </div>
              <div className="sl-news-field">
                <label className="sl-news-field-label">Meta Description</label>
                <textarea
                  className="sl-news-meta-textarea"
                  placeholder="SEO description..."
                  value={form.meta_description}
                  onChange={(e) => handleChange('meta_description', e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="sl-news-editor-sidebar">
          {/* Publish Card */}
          <div className="sl-admin-card sl-news-publish-card">
            <h3 className="sl-news-sidebar-title">Publish</h3>

            <div className="sl-news-publish-status">
              <span className={`sl-news-status ${form.is_published ? 'sl-news-status--published' : 'sl-news-status--draft'}`}>
                {form.is_published ? 'Published' : 'Draft'}
              </span>
            </div>

            <div className="sl-news-publish-actions">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSave(false)}
                disabled={saving}
                isLoading={saving && !form.is_published}
              >
                Save Draft
              </Button>
              <Button
                size="sm"
                onClick={() => handleSave(!form.is_published)}
                disabled={saving}
                isLoading={saving}
              >
                {form.is_published ? 'Unpublish' : 'Publish'}
              </Button>
            </div>

            {!isNew && (
              <button
                className="sl-news-delete-btn"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Post'}
              </button>
            )}
          </div>

          {/* Categories & Tags */}
          <div className="sl-admin-card">
            <h3 className="sl-news-sidebar-title">Organization</h3>

            <div className="sl-news-field">
              <label className="sl-news-field-label">Categories</label>
              <input
                type="text"
                className="sl-news-meta-input"
                placeholder="e.g. Updates, Patch Notes"
                value={form.categories}
                onChange={(e) => handleChange('categories', e.target.value)}
              />
              <span className="sl-news-field-hint">Comma-separated</span>
            </div>

            <div className="sl-news-field">
              <label className="sl-news-field-label">Tags</label>
              <input
                type="text"
                className="sl-news-meta-input"
                placeholder="e.g. halo, season-2, competitive"
                value={form.tags}
                onChange={(e) => handleChange('tags', e.target.value)}
              />
              <span className="sl-news-field-hint">Comma-separated</span>
            </div>
          </div>

          {/* Featured Image */}
          <div className="sl-admin-card">
            <h3 className="sl-news-sidebar-title">Featured Image</h3>
            <div className="sl-news-field">
              {form.featured_image_url ? (
                <div className="sl-news-image-wrap">
                  <img
                    src={form.featured_image_url}
                    alt="Featured"
                    className="sl-news-image-preview"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                  <button
                    type="button"
                    className="sl-news-image-remove"
                    onClick={handleImageDelete}
                    disabled={uploading || isNew}
                  >
                    {uploading ? 'Removing…' : '✕ Remove'}
                  </button>
                </div>
              ) : (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="sl-news-image-input"
                    onChange={handleImageUpload}
                    disabled={uploading || isNew}
                  />
                  {isNew && (
                    <span className="sl-news-field-hint">Save the post first to upload an image.</span>
                  )}
                  {uploading && <span className="sl-news-field-hint">Uploading…</span>}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
